import { Button } from '@/components/ui/Button';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import {
  AuditLogInsertInput,
  Customer,
  CustomerPkColumnsInput,
  CustomerSetInput,
} from '@/generated/graphql';
import { execute } from '@/graphql/execute';
import { AuditLogAction, AuditLogEntityType } from '@/models/audit-log.models';
import { CustomerConfig } from '@/models/customer.models';
import { createAuditLogInput } from '@/utils/audit-log.function';
import { useMutation } from '@tanstack/react-query';
import { Copy, Pencil } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { v4 as uuid } from 'uuid';
import {
  applyCustomerSecretConfigValues,
  buildCustomerSecretOperations,
  createDefaultCustomerSecretDrafts,
  createSecretUpsertDraft,
  CustomerSecretDrafts,
} from '../../customerSecretStore';
import {
  deleteCustomerSecretMutation,
  updateCustomerMutation,
  upsertCustomerSecretMutation,
} from '../../graphql/mutation';
import CustomerDetailsForm, {
  CustomerFormMode,
  CustomerFormValues,
  normalizeCustomerConfig,
} from '../CustomerDetailsForm';

type CustomerConfigTabProps = {
  customer: Customer;
  refetch: () => Promise<unknown>;
  refetchLog: () => Promise<unknown>;
};

export const CustomerConfigTab: React.FC<CustomerConfigTabProps> = ({
  customer,
  refetch,
  refetchLog,
}) => {
  const [editing, setEditing] = useState(false);
  const [formMode, setFormMode] = useState<CustomerFormMode>('form');
  const [secretDrafts, setSecretDrafts] = useState<CustomerSecretDrafts>(
    createDefaultCustomerSecretDrafts(),
  );

  const {
    register,
    reset,
    getValues,
    setError,
    control,
    formState: { isValid },
  } = useForm<CustomerFormValues>({});

  useEffect(() => {
    if (customer) {
      reset({
        name: customer.name,
        storagePath: customer.storagePath,
        config: normalizeCustomerConfig(customer.config),
        jsonConfig: customer.config,
      });
      setSecretDrafts(createDefaultCustomerSecretDrafts());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer]);

  const { mutateAsync: updateCustomer } = useMutation({
    mutationKey: ['UpdateCustomer'],
    mutationFn: ({
      setInput,
      id,
      auditLogInput,
    }: {
      setInput: CustomerSetInput;
      id: string;
      auditLogInput: AuditLogInsertInput;
    }) => {
      const pkColumnInput: CustomerPkColumnsInput = { id };
      return execute({
        query: updateCustomerMutation,
        variables: { pkColumnInput, setInput, auditLogInput },
      });
    },
    onSuccess: async () => {
      await refetch();
      await refetchLog();
      toast.success('Customer updated successfully!', { duration: 3000 });
      setSecretDrafts(createDefaultCustomerSecretDrafts());
      setEditing(false);
    },
  });

  const handleFormSubmit = async () => {
    const customerId = customer.id;
    const secretOperations = buildCustomerSecretOperations(secretDrafts);
    const secretConfigValues: Record<string, string> = {};

    for (const secretOperation of secretOperations) {
      try {
        if (secretOperation.type === 'upsert') {
          const upsertResult = await execute({
            query: upsertCustomerSecretMutation,
            variables: {
              input: {
                customerId,
                pathTail: secretOperation.target.pathTail,
                secretJson: secretOperation.secretJson,
              },
              invalidateCacheInput: {
                customerId,
                pathTail: secretOperation.target.pathTail,
              },
              auditLogInput: createAuditLogInput({
                action: AuditLogAction.UPSERT_CUSTOMER_SECRET,
                entities: [
                  { id: customerId, entity: AuditLogEntityType.CUSTOMER },
                ],
              }),
            },
          });
          secretConfigValues[secretOperation.target.id] =
            upsertResult.upsertCustomerSecret.uri;
          continue;
        }

        await execute({
          query: deleteCustomerSecretMutation,
          variables: {
            input: {
              customerId,
              pathTail: secretOperation.target.pathTail,
            },
            auditLogInput: createAuditLogInput({
              action: AuditLogAction.DELETE_CUSTOMER_SECRET,
              entities: [
                { id: customerId, entity: AuditLogEntityType.CUSTOMER },
              ],
            }),
          },
        });
        secretConfigValues[secretOperation.target.id] = '';
      } catch (error) {
        toast.error(`Failed to update ${secretOperation.target.label}.`, {
          duration: 4000,
          description: error instanceof Error ? error.message : undefined,
        });
        return;
      }
    }

    const formValues = getValues();
    const values = { ...formValues };
    delete values.jsonConfig;

    const serializeSystemPrompts = (config: CustomerConfig) => {
      const serializeLlm = <T extends { SystemPrompt: string }>(llm: T): T => ({
        ...llm,
        SystemPrompt: llm.SystemPrompt
          ? JSON.stringify(llm.SystemPrompt)
          : undefined,
      });
      const ar = config.AlertReviewer;
      if (!ar) return config;
      return {
        ...config,
        AlertReviewer: {
          ...ar,
          Gpt: serializeLlm(ar.Gpt),
          Claude: serializeLlm(ar.Claude),
          Gemini: serializeLlm(ar.Gemini),
        },
      };
    };

    const baseConfig =
      formMode === 'form'
        ? serializeSystemPrompts({ ...formValues.config })
        : { ...formValues.jsonConfig };
    const configWithSecretValues = applyCustomerSecretConfigValues(
      baseConfig,
      secretConfigValues,
    );

    console.log('configWithSecretValues', configWithSecretValues);
    console.log('formValue', formValues);
    // return;

    await updateCustomer({
      id: customerId,
      setInput: {
        ...values,
        config: configWithSecretValues,
        rowVersion: uuid(),
      },
      auditLogInput: createAuditLogInput({
        action: AuditLogAction.UPDATE_CUSTOMER,
        entities: [{ id: customerId, entity: AuditLogEntityType.CUSTOMER }],
      }),
    });
  };

  const handleToggleEditing = () => {
    setEditing(!editing);
    if (editing) {
      reset();
      setSecretDrafts(createDefaultCustomerSecretDrafts());
    }
  };

  return (
    <Card className="relative">
      <CardHeader>
        <CardTitle>Configuration</CardTitle>
        <Button
          className="absolute top-4 right-4"
          variant={'outline'}
          size={'sm'}
          onClick={handleToggleEditing}
        >
          {editing ? (
            'Cancel Editing'
          ) : (
            <>
              <Pencil />
              Edit
            </>
          )}
        </Button>
      </CardHeader>
      <CardContent className="grid gap-6">
        <div className="relative">
          <Input
            readOnly
            type="text"
            id="id"
            placeholder="Id"
            defaultValue={customer.id}
            className="truncate pr-8"
          />
          <Button
            type="button"
            className="text-muted-foreground hover:text-primary absolute top-0 right-0 hover:cursor-pointer"
            size={'icon'}
            variant={'link'}
            onClick={() => {
              toast.message('Copied!', { duration: 1000 });
              void navigator.clipboard.writeText(customer.id);
            }}
          >
            <Copy className="size-5" />
          </Button>
        </div>
        <CustomerDetailsForm
          register={register}
          control={control}
          setError={setError}
          readOnly={!editing}
          formMode={formMode}
          setFormMode={setFormMode}
          secretDrafts={secretDrafts}
          onStageSecretUpsert={(targetId, credentials) => {
            setSecretDrafts((t) => ({
              ...t,
              [targetId]: createSecretUpsertDraft(credentials),
            }));
          }}
          onStageSecretDelete={(targetId) => {
            setSecretDrafts((t) => ({
              ...t,
              [targetId]: { action: 'delete' },
            }));
          }}
        />
      </CardContent>
      {editing && (
        <CardFooter>
          <div className="mt-4 flex w-full justify-end gap-4">
            <Button
              variant={'outline'}
              className="w-30"
              size={'sm'}
              onClick={handleToggleEditing}
            >
              Cancel
            </Button>
            <Button
              disabled={!isValid}
              variant={'default'}
              className="w-30"
              onClick={() => void handleFormSubmit()}
              size={'sm'}
            >
              Save
            </Button>
          </div>
        </CardFooter>
      )}
    </Card>
  );
};
